import { create } from 'zustand';
import { api } from '../api/client';
import type { TipoPerfil } from '../types/models';
import { clearClientSession } from '../auth/sessionCleanup';
import { ROUTES } from '../api/routes';

/** Dados do usuario autenticado */
export interface User {
  id: string;
  email: string;
  nome: string;
  cpf?: string | null;
  dataNascimento?: string | null;
  telefone?: string | null;
  perfil: TipoPerfil;
  roles: string[];
  role?: string;
  cargo?: string; // Sub-perfil: DIRETOR | COORDENACAO | SECRETARIA
  escolaId?: string;
  nomeEscola?: string;
  municipioId?: string;
  estadoId?: string;
  redeId?: string;
  preferencias?: Record<string, any>;
  /** null = nunca logou antes (primeiro acesso) → redirecionar para /onboarding/welcome */
  ultimoLogin?: string | null;
  /** true = Passkey já registrada neste dispositivo */
  biometriaAtiva?: boolean;
  /** Lista de tipos de dispositivo que possuem Passkey: ['desktop', 'mobile'] */
  passKeyDevices?: string[];
  /** true se usuario tem senha cadastrada (false = conta Google only) */
  hasSenha?: boolean;
  /** URL da foto do usuario */
  fotoUrl?: string | null;
  /** true = usuario com acesso direto (pula wizard e security check) */
  acessoDireto?: boolean;
  /** PENDENTE | ATIVANDO | ATIVA — usado para evitar loop de onboarding */
  statusConta?: string;
  /** ID do responsável vinculado, se aplicável */
  responsavelId?: string | null;
  /** ID do aluno (tabela alunos) quando perfil=ALUNO */
  alunoId?: string | null;
  /** Flag indicando se o e-mail do usuário está verificado */
  emailVerificado?: boolean;
  /** Token FCM para push notifications de segurança */
  fcmToken?: string | null;
  /** Sessão de simulação / cockpit */
  isSimulation?: boolean;
  simulationSessionId?: string | null;
}

/** Estado global de autenticacao (Zustand) */
export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** escolaId do usuario logado — usado para montar URLs da API */
  escolaId: string | null;
  nomeEscola: string | null;
  /** perfil do usuario para roteamento por perfil */
  perfil: TipoPerfil | null;
  /** cargo do usuario (sub-perfil ESCOLA) */
  cargo: string | null;
  /** true quando esta em sessao impersonada (sessionStorage) */
  isImpersonating: boolean;
  login: (token: string, userData: User) => void;
  logout: () => void | Promise<void>;
  checkAuth: () => Promise<void>;
  updatePreferencias: (prefs: Record<string, any>) => Promise<void>;
  updateUserAvatar: (fotoUrl: string) => void;
}

// ═══════════════════════════════════════════════
// STORAGE HELPERS — sessionStorage tem prioridade
// Sessoes impersonadas usam sessionStorage (isolado por aba)
// Sessoes normais usam localStorage (compartilhado entre abas)
// ═══════════════════════════════════════════════

function isImpersonatedSession(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    sessionStorage.getItem('ac_impersonate') === 'true' ||
    window.location.search.includes('impersonate_receiver=true')
  );
}

function storageGet(key: string): string | null {
  // Impersonated session in this tab? Use sessionStorage
  if (isImpersonatedSession()) {
    return sessionStorage.getItem(key);
  }
  return localStorage.getItem(key);
}

function storageSet(key: string, value: string): void {
  if (isImpersonatedSession()) {
    sessionStorage.setItem(key, value);
  } else {
    localStorage.setItem(key, value);
  }
}

function readStoredUser(): User | null {
  const saved = storageGet('ac_user');
  if (!saved) return null;
  try {
    return JSON.parse(saved);
  } catch {
    return null;
  }
}

function isPublicAuthTransitionRoute(): boolean {
  if (typeof window === 'undefined') return false;
  return [
    '/install',
    '/login',
    '/security-compliance',
    '/security/',
    '/ativar',
  ].some((prefix) => window.location.pathname.startsWith(prefix));
}

export const useAuthStore = create<AuthState>((set) => ({
  user: readStoredUser(),
  isAuthenticated: !!storageGet('ac_token'),
  isLoading: true,
  escolaId: storageGet('ac_escolaId'),
  nomeEscola: storageGet('ac_nomeEscola'),
  perfil: (storageGet('ac_perfil') as TipoPerfil) || null,
  cargo: storageGet('ac_cargo'),
  isImpersonating: isImpersonatedSession(),
  login: (token, userData) => {
    storageSet('ac_token', token);
    storageSet('ac_user', JSON.stringify(userData));
    try {
      sessionStorage.setItem('ac_login_time', new Date().toISOString());
    } catch {}
    if (userData.escolaId) {
      storageSet('ac_escolaId', userData.escolaId);
    } else {
      localStorage.removeItem('ac_escolaId');
      sessionStorage.removeItem('ac_escolaId');
    }
    if (userData.nomeEscola) {
      storageSet('ac_nomeEscola', userData.nomeEscola);
    } else {
      localStorage.removeItem('ac_nomeEscola');
      sessionStorage.removeItem('ac_nomeEscola');
    }
    if (userData.perfil) {
      storageSet('ac_perfil', userData.perfil);
    } else {
      localStorage.removeItem('ac_perfil');
      sessionStorage.removeItem('ac_perfil');
    }
    if (userData.cargo) {
      storageSet('ac_cargo', userData.cargo);
    } else {
      localStorage.removeItem('ac_cargo');
      sessionStorage.removeItem('ac_cargo');
    }
    try {
      sessionStorage.removeItem('ac_logged_out');
    } catch {}
    set({
      user: userData,
      isAuthenticated: true,
      escolaId: userData.escolaId || null,
      nomeEscola: userData.nomeEscola || null,
      perfil: userData.perfil || null,
      cargo: userData.cargo || null,
      isImpersonating: isImpersonatedSession(),
    });
  },
  logout: async () => {
    // 1. Obter o fcmToken antes de limpar o storage
    const fcmToken = localStorage.getItem('ac_fcm_token') || localStorage.getItem('fcmToken') || undefined;

    // 2. Chamar o backend para inativar a sessão e o token FCM
    try {
      await api.post('/auth/logout', { fcmToken });
    } catch (err) {
      console.warn('[authStore] Erro ao registrar logout no backend:', err);
    }

    // 3. Limpar totalmente a sessão local
    await clearClientSession();
    try {
      sessionStorage.setItem('ac_logged_out', 'true');
    } catch {}
    set({ user: null, isAuthenticated: false, escolaId: null, nomeEscola: null, perfil: null, cargo: null, isImpersonating: false });
  },
  checkAuth: async () => {
    set({ isLoading: true });
    try {
      if (storageGet('ac_token')) {
        try {
          if (!sessionStorage.getItem('ac_login_time')) {
            sessionStorage.setItem('ac_login_time', new Date().toISOString());
          }
        } catch {}
        const { data: raw } = await api.get(ROUTES.auth.me);
        // Normalizar: backend /auth/me retorna "role" (JWT strategy), login retorna "perfil"
        const data = {
          ...raw,
          perfil: raw.perfil || raw.role,
          roles: raw.roles || [raw.perfil || raw.role],
        };
        storageSet('ac_user', JSON.stringify(data));
        if (data.escolaId) {
          storageSet('ac_escolaId', data.escolaId);
        } else {
          localStorage.removeItem('ac_escolaId');
          sessionStorage.removeItem('ac_escolaId');
        }
        if (data.nomeEscola) {
          storageSet('ac_nomeEscola', data.nomeEscola);
        } else {
          localStorage.removeItem('ac_nomeEscola');
          sessionStorage.removeItem('ac_nomeEscola');
        }
        if (data.perfil) {
          storageSet('ac_perfil', data.perfil);
        } else {
          localStorage.removeItem('ac_perfil');
          sessionStorage.removeItem('ac_perfil');
        }
        if (data.cargo) {
          storageSet('ac_cargo', data.cargo);
        } else {
          localStorage.removeItem('ac_cargo');
          sessionStorage.removeItem('ac_cargo');
        }
        set({
          user: data,
          isAuthenticated: true,
          escolaId: data.escolaId || null,
          nomeEscola: data.nomeEscola || null,
          perfil: data.perfil || null,
          cargo: data.cargo || null,
          isImpersonating: isImpersonatedSession(),
        });
      } else {
        set({ user: null, isAuthenticated: false });
      }
    } catch (e: any) {
      const token = storageGet('ac_token');
      const savedUser = readStoredUser();
      const is401Unauthorized = e?.response?.status === 401;

      // Se houver token/savedUser e o erro NÃO for 401 definitivo (ex: 429 rate limit, 5xx ou offline),
      // ou se for impersonação/transição pública: NÃO limpa a sessão local para evitar deslogamento espúrio.
      if (token && (!is401Unauthorized || isImpersonatedSession() || isPublicAuthTransitionRoute())) {
        set({
          user: savedUser,
          isAuthenticated: !!savedUser || !!token,
          escolaId: savedUser?.escolaId || storageGet('ac_escolaId'),
          nomeEscola: savedUser?.nomeEscola || storageGet('ac_nomeEscola'),
          perfil: savedUser?.perfil || ((storageGet('ac_perfil') as TipoPerfil) || null),
          cargo: savedUser?.cargo || storageGet('ac_cargo'),
          isImpersonating: isImpersonatedSession(),
        });
        return;
      }

      set({ user: null, isAuthenticated: false, perfil: null, cargo: null, isImpersonating: false });
      clearClientSession().catch(() => {});
    } finally {
      set({ isLoading: false });
    }
  },
  updatePreferencias: async (prefs) => {
    try {
      const { data } = await api.patch(ROUTES.auth.mePreferencias, prefs);
      set((state) => ({
        user: state.user ? { ...state.user, preferencias: data.preferencias } : null,
      }));
    } catch (e) {
      console.warn('[Prefs] Falha ao salvar preferencias:', e);
    }
  },
  updateUserAvatar: (fotoUrl: string) => {
    set((state) => {
      if (!state.user) return state;
      const updatedUser = { ...state.user, fotoUrl };
      try {
        storageSet('ac_user', JSON.stringify(updatedUser));
      } catch (e) { console.warn('[authStore] Erro ao persistir ac_user no storage:', String(e)); }
      return { user: updatedUser };
    });
  },
}));
